import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, Lightbulb, Globe2, Award, Loader2 } from 'lucide-react';
import { companyApi, aboutPageApi, brandsApi } from '../services/api';
import RichTextContent from '../components/RichTextContent';

const AboutPage = () => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [aboutSettings, setAboutSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, aboutRes, brandsRes] = await Promise.all([
          companyApi.get(),
          aboutPageApi.get(),
          brandsApi.getAll()
        ]);
        setCompanyInfo(companyRes.data);
        setAboutSettings(aboutRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount — API imports are stable module-level refs

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const settings = aboutSettings || {};

  const services = [
    {
      icon: Lightbulb,
      title: settings.service1Title || 'Product Specification',
      description: settings.service1Description || 'Expert guidance on selecting the perfect lighting solutions for any project, from initial concept to final specification.'
    },
    {
      icon: Users,
      title: settings.service2Title || 'Technical Support',
      description: settings.service2Description || 'In-depth technical assistance and consultation throughout the project lifecycle, ensuring optimal implementation.'
    },
    {
      icon: Globe2,
      title: settings.service3Title || 'Project Coordination',
      description: settings.service3Description || 'Seamless coordination between designers, architects, and contractors to deliver exceptional lighting installations.'
    }
  ];

  const stats = [
    { value: settings.stat1Value || `${brands.length}+`, label: settings.stat1Label || 'Partner Brands' },
    { value: settings.stat2Value || '15+', label: settings.stat2Label || 'Countries Served' },
    { value: settings.stat3Value || '20+', label: settings.stat3Label || 'Years Experience' },
    { value: settings.stat4Value || '100+', label: settings.stat4Label || 'Design Partners' }
  ];

  return (
    <div className="bg-white" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gray-900 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">About</span>
          </nav>

          <h1 className="text-4xl md:text-5xl font-light text-white mb-4">
            {settings.heroTitle || 'About Us'}
          </h1>
          {settings.heroSubtitle && (
            <p className="text-xl text-gray-400 max-w-2xl">
              {settings.heroSubtitle}
            </p>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-8">
                {settings.mainHeading || 'Bringing World-Class Lighting'}{' '}
                <span className="text-gray-500">{settings.mainHeadingHighlight || 'to the UK Market'}</span>
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <RichTextContent
                  content={settings.mainContent || `${companyInfo?.name || 'Mesh Lighting Ltd'} is a specialist architectural lighting agency representing some of Europe's most innovative lighting manufacturers in the United Kingdom.`}
                />
                <RichTextContent
                  content={settings.secondaryContent || 'With decades of combined experience in architectural lighting, our team provides comprehensive specification support to lighting designers, architects, and interior designers working on prestigious projects across the UK.'}
                />
                <RichTextContent
                  content={settings.tertiaryContent || "Our carefully curated portfolio of manufacturers represents the finest in European lighting design and technology, from technical LED solutions to decorative fixtures."}
                />
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={settings.heroImage || "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"}
                  alt="About Mesh Lighting"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Stats overlay */}
              <div className="absolute -bottom-8 -left-8 bg-gray-900 text-white p-8 rounded-lg shadow-xl">
                <div className="grid grid-cols-2 gap-6">
                  {stats.slice(0, 2).map((stat) => (
                    <div key={stat.label}>
                      <div className="text-3xl font-light">{stat.value}</div>
                      <div className="text-sm text-gray-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              {settings.servicesTitle || 'What We Do'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {settings.servicesSubtitle || 'We provide comprehensive support to lighting professionals, from initial specification to project completion.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.title}
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-3">{service.title}</h3>
                <RichTextContent content={service.description} className="text-gray-600" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Brands */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-light text-gray-900 mb-4">Our Partner Brands</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We represent a carefully selected portfolio of leading European lighting manufacturers.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to={`/brand/${brand.id}`}
                className="flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
              >
                <div className="text-center">
                  <div className="text-lg font-medium text-gray-900 group-hover:text-gray-700">
                    {brand.name}
                  </div>
                  <div className="text-sm text-gray-500">{brand.country}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Full Stats Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl md:text-5xl font-light text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="h-12 w-12 text-gray-400 mx-auto mb-6" />
          <h2 className="text-3xl font-light text-gray-900 mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Get in touch with our team to discuss how we can help bring your lighting vision to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors"
          >
            Contact Us
            <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
